import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import AdminModalTailwind from "./AdminModalTailwind";
import CategoryFormTailwind from "./CategoryFormTailwind";
import ConfirmationModal from "./ConfirmationModal";
import GenericSelectTailwind from "./GenericSelectTailwind";
import Toast from "./Toast";
import { categoriesService, ageGroupsService, pdfExportService } from "../../services";
import { getAgeGroupColors, getAgeGroupBadgeClasses, sortAgeGroups } from "../../utils/ageGroupColors";

const CategoriesTailwind = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedType, setSelectedType] = useState("all");
  const [selectedAgeGroup, setSelectedAgeGroup] = useState("all");
  const [categories, setCategories] = useState([]);
  const [ageGroups, setAgeGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Modal und Form States
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const categoryFormRef = useRef();

  // Confirmation Modal States
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);

  // Toast Handler
  const [toast, setToast] = useState({ show: false, message: "", type: "info" });

  // Export/Import States
  const [isExporting, setIsExporting] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [isImporting, setIsImporting] = useState(false);

  // Pagination States
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Lade Daten beim ersten Laden
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Lade Kategorien und Altersgruppen parallel
      const [categoriesResponse, ageGroupsResponse] = await Promise.all([
        categoriesService.getAllCategories(),
        ageGroupsService.getAllAgeGroups()
      ]);

      if (categoriesResponse.success) {
        setCategories(categoriesResponse.data);
      } else {
        setError(categoriesResponse.error);
      }

      if (ageGroupsResponse.success) {
        setAgeGroups(ageGroupsResponse.data);
      } else {
        setError(ageGroupsResponse.error);
      }
    } catch (err) {
      setError("Fehler beim Laden der Daten");
      console.error("Fehler beim Laden der Daten:", err);
    } finally {
      setLoading(false);
    }
  };

  // Event Handlers
  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleTypeChange = (type) => {
    setSelectedType(type);
  };

  const handleAgeGroupChange = (ageGroup) => {
    setSelectedAgeGroup(ageGroup);
  };

  const handleAddCategory = () => {
    setEditingCategory(null);
    setShowModal(true);
  };

  const handleEditCategory = (category) => {
    setEditingCategory(category);
    setIsFormValid(true); // Beim Bearbeiten ist das Formular initial valide
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingCategory(null);
  };

  const handleSaveCategory = async (categoryData) => {
    setIsSaving(true);
    try {
      if (editingCategory) {
        // Update existing category
        const response = await categoriesService.updateCategory(editingCategory.id, categoryData);
        if (response.success) {
          addToast('success', 'Kategorie erfolgreich aktualisiert');
          loadData();
          handleCloseModal();
        } else {
          addToast('error', response.error || 'Fehler beim Aktualisieren der Kategorie');
        }
      } else {
        // Create new category
        const response = await categoriesService.createCategory(categoryData);
        if (response.success) {
          addToast('success', 'Kategorie erfolgreich erstellt');
          loadData();
          handleCloseModal();
        } else {
          addToast('error', response.error || 'Fehler beim Erstellen der Kategorie');
        }
      }
    } catch (err) {
      addToast('error', 'Fehler beim Speichern der Kategorie');
      console.error("Fehler beim Speichern:", err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteCategory = (category) => {
    setCategoryToDelete(category);
    setShowConfirmation(true);
  };

  const handleConfirmDelete = async () => {
    if (!categoryToDelete) return;

    try {
      const response = await categoriesService.deleteCategory(categoryToDelete.id);
      if (response.success) {
        addToast('success', 'Kategorie erfolgreich gelöscht');
        loadData();
      } else {
        addToast('error', response.error || 'Fehler beim Löschen der Kategorie');
      }
    } catch (err) {
      addToast('error', 'Fehler beim Löschen der Kategorie');
      console.error("Fehler beim Löschen:", err);
    } finally {
      handleCloseConfirmation();
    }
  };

  const handleCloseConfirmation = () => {
    setShowConfirmation(false);
    setCategoryToDelete(null);
  };

  // Toast Handler
  const addToast = (type, message) => {
    setToast({ show: true, message, type });
    
    // Toast nach 5 Sekunden automatisch entfernen
    setTimeout(() => {
      setToast({ show: false, message: "", type: "info" });
    }, 5000);
  };

  const hideToast = () => {
    setToast({ show: false, message: "", type: "info" });
  };

  // Export Handler
  const handleExportPDF = async () => {
    setIsExporting(true);
    setShowExportMenu(false);
    try {
      const filters = {
        type: selectedType,
        ageGroup: selectedAgeGroup,
        searchTerm: searchTerm
      };

      const result = await pdfExportService.exportCategoriesPDF(filters);
      
      if (result.success) {
        addToast('success', 'PDF erfolgreich heruntergeladen');
      } else {
        addToast('error', result.error || 'Fehler beim PDF-Export');
      }
    } catch (error) {
      addToast('error', 'Fehler beim PDF-Export');
      console.error("Fehler beim PDF-Export:", error);
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportJSON = async () => {
    setIsExporting(true);
    setShowExportMenu(false);
    try {
      const filters = {
        type: selectedType,
        ageGroup: selectedAgeGroup,
        searchTerm: searchTerm
      };

      const result = await pdfExportService.exportCategoriesJSON(filters);
      
      if (result.success) {
        addToast('success', 'JSON erfolgreich heruntergeladen');
      } else {
        addToast('error', result.error || 'Fehler beim JSON-Export');
      }
    } catch (error) {
      addToast('error', 'Fehler beim JSON-Export');
      console.error("Fehler beim JSON-Export:", error);
    } finally {
      setIsExporting(false);
    }
  };

  // Import Handler
  const handleImportJSON = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.json')) {
      addToast('error', 'Bitte wählen Sie eine JSON-Datei aus');
      return;
    }

    setIsImporting(true);
    try {
      const fileContent = await file.text();
      const jsonData = JSON.parse(fileContent);

      // Unterstütze beide Formate: direktes Array oder { data: [...] } oder { categories: [...] }
      let categoriesArray = [];
      if (Array.isArray(jsonData)) {
        categoriesArray = jsonData;
      } else if (jsonData.data && Array.isArray(jsonData.data)) {
        categoriesArray = jsonData.data;
      } else if (jsonData.categories && Array.isArray(jsonData.categories)) {
        categoriesArray = jsonData.categories;
      } else {
        throw new Error('Ungültiges JSON-Format. Erwartet wird ein Array oder ein Objekt mit "data" oder "categories"');
      }

      const result = await pdfExportService.importCategoriesJSON(categoriesArray);
      
      if (result.success) {
        let message = result.message || 'JSON erfolgreich importiert';
        if (result.data) {
          message = `${message} (${result.data.created || 0} erstellt, ${result.data.updated || 0} aktualisiert${result.data.errors > 0 ? `, ${result.data.errors} Fehler` : ''})`;
        }
        addToast('success', message);
        // Daten neu laden
        await loadData();
      } else {
        addToast('error', result.error || 'Fehler beim JSON-Import');
      }
    } catch (error) {
      addToast('error', 'Fehler beim Importieren der JSON-Datei: ' + error.message);
      console.error("Fehler beim JSON-Import:", error);
    } finally {
      setIsImporting(false);
      // Reset file input
      event.target.value = '';
    }
  };

  // Schließe Export-Menü beim Klicken außerhalb
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showExportMenu && !event.target.closest('.relative')) {
        setShowExportMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showExportMenu]);

  // Funktion für Altersgruppen-Farben (verwendet jetzt die Utility)
  const getAgeGroupColor = (ageGroup) => {
    return getAgeGroupColors(ageGroup).name;
  };

  const typeOptions = [
    { value: "all", label: t("admin.categories.allTypes", "Alle Typen") },
    { value: "Handform", label: t("admin.categories.handform", "Handform") },
    { value: "Waffenform", label: t("admin.categories.waffenform", "Waffenform") },
    { value: "Offene Form", label: t("admin.categories.offeneform", "Offene Form") },
    {value:"Partnerform", label: t("admin.categories.partnerform", "Partnerform")}
  ];

  const ageGroupOptions = [
    { value: "all", label: t("admin.categories.allAgeGroups", "Alle Altersgruppen") },
    ...sortAgeGroups(ageGroups).map((ageGroup) => ({
      value: ageGroup.name,
      label: ageGroup.name
    }))
  ];

  const filteredCategories = categories.filter((item) => {
    const matchesSearch = item.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = selectedType === "all" || item.type === selectedType;
    const matchesAgeGroup = selectedAgeGroup === "all" || item.ageGroups.includes(selectedAgeGroup);
    return matchesSearch && matchesType && matchesAgeGroup;
  });

  // Pagination Logic
  const totalItems = filteredCategories.length;
  const totalPages = itemsPerPage === "all" ? 1 : Math.ceil(totalItems / itemsPerPage);
  const startIndex = itemsPerPage === "all" ? 0 : (currentPage - 1) * itemsPerPage;
  const endIndex = itemsPerPage === "all" ? totalItems : startIndex + itemsPerPage;
  const paginatedCategories = itemsPerPage === "all" 
    ? filteredCategories 
    : filteredCategories.slice(startIndex, endIndex);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedType, selectedAgeGroup, itemsPerPage]);

  // Scroll to top when page changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentPage]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleItemsPerPageChange = (value) => {
    setItemsPerPage(value === "all" ? "all" : parseInt(value));
    setCurrentPage(1);
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Active Filters
  const getActiveFilters = () => {
    const filters = [];
    if (searchTerm) {
      filters.push({
        type: "Suche",
        value: searchTerm,
        onRemove: () => setSearchTerm("")
      });
    }
    if (selectedType !== "all") {
      const typeLabel = typeOptions.find(opt => opt.value === selectedType)?.label || selectedType;
      filters.push({
        type: "Typ",
        value: typeLabel,
        onRemove: () => setSelectedType("all")
      });
    }
    if (selectedAgeGroup !== "all") {
      filters.push({
        type: "Altersgruppe",
        value: selectedAgeGroup,
        onRemove: () => setSelectedAgeGroup("all")
      });
    }
    return filters;
  };

  const handleResetFilters = () => {
    setSelectedType("all");
    setSelectedAgeGroup("all");
    setSearchTerm("");
    setCurrentPage(1);
  };

  const handleViewGroups = (category) => {
    navigate(`/admin/groups?category=${encodeURIComponent(category.name)}`);
  };

  if (loading) {
    return (
      <main className="pt-24 pb-8 bg-slate-50/50 dark:bg-slate-900 min-h-screen transition-colors">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header Skeleton */}
          <div className="mb-8 animate-pulse">
            <div className="flex items-center gap-3 mb-2">
              <div className="h-9 bg-slate-200 dark:bg-slate-700 rounded w-64"></div>
              <div className="h-6 w-12 bg-slate-200 dark:bg-slate-700 rounded-full"></div>
            </div>
            <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-80"></div>
          </div>

          {/* Controls Skeleton */}
          <div className="mb-6 flex items-center justify-end gap-2 animate-pulse">
            <div className="w-28 h-9 bg-slate-200 dark:bg-slate-700 rounded-xl"></div>
            <div className="w-px h-5 bg-slate-200 dark:bg-slate-700"></div>
            <div className="w-22 h-9 bg-slate-200 dark:bg-slate-700 rounded-xl"></div>
            <div className="w-22 h-9 bg-slate-200 dark:bg-slate-700 rounded-xl"></div>
          </div>

          {/* Filter Section Skeleton */}
          <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm p-6 mb-6 animate-pulse">
            {/* Search Bar Skeleton */}
            <div className="mb-6">
              <div className="h-[46px] bg-slate-200 dark:bg-slate-700 rounded-xl"></div>
            </div>

            {/* Filter Dropdowns Skeleton - 2 columns */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              {[...Array(2)].map((_, i) => (
                <div key={i}>
                  <div className="h-3 bg-slate-200 dark:bg-slate-700 rounded w-16 mb-2"></div>
                  <div className="h-[46px] bg-slate-200 dark:bg-slate-700 rounded-xl"></div>
                </div>
              ))}
            </div>
          </div>

          {/* Table Skeleton */}
          <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden animate-pulse">
            <div className="overflow-x-auto">
              <table className="w-full divide-y divide-slate-200 dark:divide-slate-700">
                <thead className="border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-700/50">
                  <tr>
                    {[...Array(5)].map((_, i) => (
                      <th key={i} className="px-6 py-3">
                        <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-20"></div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                  {[...Array(8)].map((_, i) => (
                    <tr key={i}>
                      {[...Array(5)].map((_, j) => (
                        <td key={j} className="px-6 py-4">
                          <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-full"></div>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
                {/* Pagination Footer Skeleton */}
                <tfoot className="bg-slate-50 dark:bg-slate-700/30">
                  <tr>
                    <td colSpan={5} className="px-6 py-4">
                      <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                        <div className="flex items-center gap-3">
                          <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-12"></div>
                          <div className="h-9 w-20 bg-slate-200 dark:bg-slate-700 rounded-xl"></div>
                          <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-24"></div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-32"></div>
                          <div className="h-9 w-9 bg-slate-200 dark:bg-slate-700 rounded-xl"></div>
                          <div className="flex items-center gap-2">
                            <div className="h-9 w-9 bg-slate-200 dark:bg-slate-700 rounded-xl"></div>
                            {[...Array(5)].map((_, i) => (
                              <div key={i} className="h-9 w-9 bg-slate-200 dark:bg-slate-700 rounded-xl"></div>
                            ))}
                            <div className="h-9 w-9 bg-slate-200 dark:bg-slate-700 rounded-xl"></div>
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>
      </main>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 transition-colors">
        <main className="pt-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-full mb-4">
                  <i className="fas fa-exclamation-triangle text-red-600 dark:text-red-400"></i>
                </div>
                <p className="text-slate-600 dark:text-slate-400 font-medium mb-4">{error}</p>
                <button
                  onClick={loadData}
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary hover:bg-primary-dark dark:bg-primary-light dark:hover:bg-primary text-white text-sm font-medium rounded-xl transition-colors shadow-sm"
                >
                  {t("admin.retry", "Erneut versuchen")}
                </button>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <>
      <main className="pt-24 pb-8 bg-slate-50/50 dark:bg-slate-900 min-h-screen transition-colors">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header Section */}
          <div className="mb-8">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-3 flex-wrap">
                  <h1 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100">
                    {t("admin.categories.title", "Kategorienverwaltung")}
                  </h1>
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-600">
                    <i className="fas fa-layer-group text-[10px]"></i>
                    {filteredCategories.length}
                  </span>
                </div>
                <p className="text-slate-500 dark:text-slate-400 mt-1.5 text-sm">
                  {t("admin.categories.subtitle", "Verwalte alle Wettkampfkategorien und deren Einstellungen.")}
                </p>
              </div>
            </div>
          </div>

          {/* Controls Section */}
          <div className="mb-6 flex items-center justify-end gap-2">
            <button
              onClick={handleAddCategory}
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary dark:bg-primary-light text-white text-sm font-medium rounded-xl hover:bg-primary-dark dark:hover:bg-primary transition-colors shadow-sm"
            >
              <i className="fas fa-plus text-xs"></i>
              {t("admin.categories.add", "Hinzufügen")}
            </button>

            <div className="h-5 w-px bg-slate-200 dark:bg-slate-700"></div>

            <div className="relative">
              <button
                onClick={() => setShowExportMenu(!showExportMenu)}
                disabled={isExporting}
                className="inline-flex items-center gap-2 px-3.5 py-2 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 text-sm font-medium rounded-xl border border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <i className={`fas ${isExporting ? 'fa-spinner fa-spin' : 'fa-download'} text-xs`}></i>
                {isExporting ? 'Export...' : 'Export'}
                {!isExporting && <i className="fas fa-chevron-down text-[10px] opacity-60"></i>}
              </button>
              {showExportMenu && !isExporting && (
                <div className="absolute right-0 mt-1.5 w-52 bg-white dark:bg-slate-800 rounded-xl shadow-lg border border-slate-200 dark:border-slate-700 z-10 overflow-hidden">
                  <button
                    onClick={handleExportPDF}
                    className="w-full text-left px-4 py-2.5 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors flex items-center gap-2.5"
                  >
                    <i className="fas fa-file-pdf text-red-500 text-xs w-3.5 text-center"></i>
                    Als PDF exportieren
                  </button>
                  <button
                    onClick={handleExportJSON}
                    className="w-full text-left px-4 py-2.5 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors flex items-center gap-2.5 border-t border-slate-100 dark:border-slate-700/50"
                  >
                    <i className="fas fa-file-code text-blue-500 text-xs w-3.5 text-center"></i>
                    Als JSON exportieren
                  </button>
                </div>
              )}
            </div>

            <label className="inline-flex items-center gap-2 px-3.5 py-2 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 text-sm font-medium rounded-xl border border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors cursor-pointer">
              <i className={`fas ${isImporting ? 'fa-spinner fa-spin' : 'fa-upload'} text-xs`}></i>
              {isImporting ? 'Import...' : 'Import'}
              <input
                type="file"
                accept=".json"
                onChange={handleImportJSON}
                disabled={isImporting}
                className="hidden"
              />
            </label>
          </div>

        {/* Filter Section */}
        <div id="filter-section" className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm p-5 mb-6">
          {/* Search Bar */}
          <div className="mb-5">
            <div className="relative">
              <input
                type="text"
                placeholder={t("admin.categories.search", "Kategorien suchen…")}
                value={searchTerm}
                onChange={handleSearch}
                className="w-full pl-10 pr-4 h-10 border border-slate-200 dark:border-slate-600 rounded-xl text-sm text-slate-900 dark:text-slate-100 bg-slate-50 dark:bg-slate-700 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary/20 dark:focus:ring-primary-light/20 focus:border-primary dark:focus:border-primary-light transition-colors"
              />
              <i className="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500 text-xs"></i>
            </div>
          </div>

          {/* Filter Dropdowns */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
            {/* Type Filter */}
            <div>
              <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400 mb-1.5">
                {t("admin.categories.type", "Typ")}
              </label>
              <GenericSelectTailwind
                value={selectedType}
                onChange={handleTypeChange}
                options={typeOptions.map(opt => ({ value: opt.value, label: opt.label }))}
                placeholder="Alle Typen"
              />
            </div>

            {/* Age Group Filter */}
            <div>
              <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400 mb-1.5">
                {t("admin.categories.ageGroups", "Altersgruppe")}
              </label>
              <GenericSelectTailwind
                value={selectedAgeGroup}
                onChange={handleAgeGroupChange}
                options={ageGroupOptions.map(opt => {
                  if (opt.value === "all") {
                    return { value: opt.value, label: opt.label };
                  }
                  const colors = getAgeGroupColors(opt.value);
                  return {
                    value: opt.value,
                    label: opt.label,
                    bgColor: colors.bgLight,
                    textColor: colors.text
                  };
                })}
                placeholder="Alle Altersgruppen"
              />
            </div>
          </div>

          {/* Active Filters Section */}
          {getActiveFilters().length > 0 && (
            <div className="border-t border-slate-100 dark:border-slate-700/60 pt-4 mt-1">
              <div className="flex items-center justify-between mb-2.5">
                <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400">
                  Aktive Filter
                </span>
                <button
                  onClick={handleResetFilters}
                  className="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-lg text-xs font-medium transition-colors border bg-white dark:bg-slate-800 text-slate-500 dark:text-slate-400 border-slate-200 dark:border-slate-600 hover:text-primary dark:hover:text-primary-light"
                >
                  <i className="fas fa-undo text-[10px]"></i>
                  Zurücksetzen
                </button>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {getActiveFilters().map((filter, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary/8 dark:bg-primary-light/10 text-primary dark:text-primary-light border border-primary/20 dark:border-primary-light/20"
                  >
                    {filter.type}: {filter.value}
                    <button onClick={filter.onRemove} className="ml-0.5 opacity-60 hover:opacity-100 transition-opacity">
                      <i className="fas fa-times text-[9px]"></i>
                    </button>
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Table */}
        {filteredCategories.length > 0 && (
          <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full divide-y divide-slate-200 dark:divide-slate-700">
                <thead className="border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-700/50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                      {t("admin.categories.id", "ID")}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                      {t("admin.categories.name", "Name")}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                      {t("admin.categories.type", "Typ")}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                      {t("admin.categories.ageGroups", "Altersgruppen")}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                      {t("admin.categories.actions", "Aktionen")}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                  {paginatedCategories.map((item) => (
                    <tr key={item.id} className="hover:bg-slate-50/50 dark:hover:bg-slate-700/50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-slate-500 dark:text-slate-400">
                        {item.id}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-slate-900 dark:text-slate-100">{item.name}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {(() => {
                          const typeConfig = {
                            'Handform':   { bg: 'bg-blue-50 dark:bg-blue-900/30',   text: 'text-blue-700 dark:text-blue-300',   border: 'border-blue-200 dark:border-blue-700/40',   icon: 'fa-hand-paper' },
                            'Waffenform': { bg: 'bg-emerald-50 dark:bg-emerald-900/30', text: 'text-emerald-700 dark:text-emerald-300', border: 'border-emerald-200 dark:border-emerald-700/40', icon: 'fa-khanda' },
                            'Offene Form':{ bg: 'bg-violet-50 dark:bg-violet-900/30', text: 'text-violet-700 dark:text-violet-300', border: 'border-violet-200 dark:border-violet-700/40', icon: 'fa-star' },
                            'Partnerform':{ bg: 'bg-orange-50 dark:bg-orange-900/30', text: 'text-orange-700 dark:text-orange-300', border: 'border-orange-200 dark:border-orange-700/40', icon: 'fa-handshake' },
                          };
                          const cfg = typeConfig[item.type] || { bg: 'bg-slate-100 dark:bg-slate-700', text: 'text-slate-600 dark:text-slate-300', border: 'border-slate-200 dark:border-slate-600/50', icon: 'fa-tag' };
                          return (
                            <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-medium border ${cfg.bg} ${cfg.text} ${cfg.border}`}>
                              <i className={`fas ${cfg.icon} text-[9px]`}></i>
                              {item.type}
                            </span>
                          );
                        })()}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-wrap gap-1">
                          {sortAgeGroups([...item.ageGroups]).map((ageGroup, index) => {
                            const colors = getAgeGroupColors(ageGroup);
                            const darkBgMap = { 'bg-emerald-100': 'dark:bg-emerald-900/30', 'bg-blue-100': 'dark:bg-blue-900/30', 'bg-violet-100': 'dark:bg-violet-900/30', 'bg-amber-100': 'dark:bg-amber-900/30', 'bg-red-100': 'dark:bg-red-900/30', 'bg-gray-100': 'dark:bg-gray-700' };
                            const darkTextMap = { 'text-emerald-700': 'dark:text-emerald-300', 'text-blue-700': 'dark:text-blue-300', 'text-violet-700': 'dark:text-violet-300', 'text-amber-700': 'dark:text-amber-300', 'text-red-700': 'dark:text-red-300', 'text-gray-800': 'dark:text-gray-300' };
                            const borderMap = { 'bg-emerald-100': 'border-emerald-200 dark:border-emerald-700/40', 'bg-blue-100': 'border-blue-200 dark:border-blue-700/40', 'bg-violet-100': 'border-violet-200 dark:border-violet-700/40', 'bg-amber-100': 'border-amber-200 dark:border-amber-700/40', 'bg-red-100': 'border-red-200 dark:border-red-700/40', 'bg-gray-100': 'border-gray-200 dark:border-gray-600/40' };
                            return (
                              <span
                                key={index}
                                className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium border ${colors.bgLight} ${darkBgMap[colors.bgLight] || 'dark:bg-slate-700'} ${colors.text} ${darkTextMap[colors.text] || 'dark:text-slate-300'} ${borderMap[colors.bgLight] || 'border-slate-200 dark:border-slate-600/40'}`}
                              >
                                {ageGroup}
                              </span>
                            );
                          })}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => handleEditCategory(item)}
                            className="w-7 h-7 flex items-center justify-center rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-500 dark:text-slate-400 hover:text-primary dark:hover:text-primary-light hover:border-primary/30 dark:hover:border-primary-light/30 transition-colors"
                            title={t("admin.categories.edit", "Bearbeiten")}
                          >
                            <i className="fas fa-edit text-[11px]"></i>
                          </button>
                          <button
                            onClick={() => handleViewGroups(item)}
                            className="w-7 h-7 flex items-center justify-center rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-500 dark:text-slate-400 hover:text-primary dark:hover:text-primary-light hover:border-primary/30 dark:hover:border-primary-light/30 transition-colors"
                            title={t("admin.categories.viewGroups", "Gruppen anzeigen")}
                          >
                            <i className="fas fa-layer-group text-[11px]"></i>
                          </button>
                          <button
                            onClick={() => handleDeleteCategory(item)}
                            className="w-7 h-7 flex items-center justify-center rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-500 dark:text-slate-400 hover:text-red-600 dark:hover:text-red-400 hover:border-red-200 dark:hover:border-red-700/50 transition-colors"
                            title={t("admin.categories.delete", "Löschen")}
                          >
                            <i className="fas fa-trash text-[11px]"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
                {/* Pagination Footer Row */}
                <tfoot className="bg-slate-50 dark:bg-slate-700/30">
                  <tr>
                    <td colSpan="5" className="px-6 py-4">
                      <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                        {/* Items per page selector */}
                        <div className="flex items-center gap-3">
                          <span className="text-sm text-slate-600 dark:text-slate-400">
                            Zeige
                          </span>
                          <div className="relative" style={{ zIndex: showModal || showConfirmation ? 1 : 100, pointerEvents: showModal || showConfirmation ? 'none' : 'auto' }}>
                            <GenericSelectTailwind
                              value={itemsPerPage.toString()}
                              onChange={(value) => handleItemsPerPageChange(value === "all" ? "all" : parseInt(value))}
                              options={[
                                { value: "10", label: "10" },
                                { value: "20", label: "20" },
                                { value: "50", label: "50" },
                                { value: "100", label: "100" },
                                { value: "all", label: "Alle" }
                              ]}
                              placeholder="10"
                              openUpward={true}
                              whiteBackground={true}
                              disabled={showModal || showConfirmation}
                            />
                          </div>
                          <span className="text-sm text-slate-600 dark:text-slate-400">
                            Einträge pro Seite
                          </span>
                        </div>

                        {/* Pagination Info and Controls */}
                        <div className="flex items-center gap-4">
                          {/* Info Text */}
                          <span className="text-sm text-slate-600 dark:text-slate-400">
                            Zeige <span className="font-semibold text-slate-900 dark:text-slate-100">{startIndex + 1}</span>-<span className="font-semibold text-slate-900 dark:text-slate-100">{Math.min(endIndex, totalItems)}</span> von <span className="font-semibold text-slate-900 dark:text-slate-100">{totalItems}</span> Kategorien
                          </span>

                          {/* Scroll to Top Button */}
                          <button
                            onClick={scrollToTop}
                            className="inline-flex items-center justify-center w-9 h-9 rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                            title="Nach oben scrollen"
                          >
                            <i className="fas fa-arrow-up text-xs"></i>
                          </button>

                          {/* Pagination Buttons */}
                          {itemsPerPage !== "all" && totalPages > 1 && (
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={currentPage === 1}
                                className="inline-flex items-center justify-center w-9 h-9 rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                <i className="fas fa-chevron-left text-xs"></i>
                              </button>

                              {/* Page Numbers */}
                              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                let pageNum;
                                if (totalPages <= 5) {
                                  pageNum = i + 1;
                                } else if (currentPage <= 3) {
                                  pageNum = i + 1;
                                } else if (currentPage >= totalPages - 2) {
                                  pageNum = totalPages - 4 + i;
                                } else {
                                  pageNum = currentPage - 2 + i;
                                }

                                return (
                                  <button
                                    key={pageNum}
                                    onClick={() => handlePageChange(pageNum)}
                                    className={`inline-flex items-center justify-center w-9 h-9 rounded-xl border transition-colors ${
                                      currentPage === pageNum
                                        ? 'bg-primary dark:bg-primary-light text-white border-primary/30 dark:border-primary-light/30'
                                        : 'border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700'
                                    }`}
                                  >
                                    {pageNum}
                                  </button>
                                );
                              })}

                              <button
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={currentPage === totalPages}
                                className="inline-flex items-center justify-center w-9 h-9 rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                <i className="fas fa-chevron-right text-xs"></i>
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        )}

        {/* Empty State */}
        {filteredCategories.length === 0 && (
          <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm text-center py-16 px-8">
            <div className="w-14 h-14 bg-slate-100 dark:bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="fas fa-layer-group text-slate-400 dark:text-slate-500 text-xl"></i>
            </div>
            <h3 className="text-base font-semibold text-slate-800 dark:text-slate-100 mb-1.5">
              {t("admin.categories.empty.title", "Keine Kategorien gefunden")}
            </h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-6 max-w-md mx-auto">
              {searchTerm || selectedType !== "all" || selectedAgeGroup !== "all"
                ? t("admin.categories.empty.filterMessage", "Versuche die Filter oder den Suchbegriff anzupassen.")
                : t("admin.categories.empty.noDataMessage", "Füge die erste Kategorie hinzu, um zu beginnen.")
              }
            </p>
            {(!searchTerm && selectedType === "all" && selectedAgeGroup === "all") && (
              <button
                onClick={handleAddCategory}
                className="inline-flex items-center gap-2 px-4 py-2.5 bg-primary dark:bg-primary-light text-white text-sm font-medium rounded-xl hover:bg-primary-dark dark:hover:bg-primary transition-colors shadow-sm"
              >
                <i className="fas fa-plus text-xs"></i>
                {t("admin.categories.empty.addButton", "Erste Kategorie hinzufügen")}
              </button>
            )}
          </div>
        )}
        </div>
      </main>

      {/* Modal für Kategorie hinzufügen/bearbeiten */}
      <AdminModalTailwind
        isOpen={showModal}
        onClose={handleCloseModal}
        title={editingCategory ? t("admin.categories.editTitle", "Kategorie bearbeiten") : t("admin.categories.addTitle", "Neue Kategorie")}
        subtitle={editingCategory ? t("admin.categories.editSubtitle", "Bearbeite die ausgewählte Kategorie.") : t("admin.categories.addSubtitle", "Erstelle eine neue Wettkampfkategorie.")}
        onSave={() => categoryFormRef.current?.handleSubmit()}
        onCancel={handleCloseModal}
        saveButtonText={editingCategory ? t("admin.update", "Aktualisieren") : t("admin.create", "Erstellen")}
        cancelButtonText={t("admin.cancel", "Abbrechen")}
        isSaving={isSaving}
        isSaveDisabled={!isFormValid}
      >
        <CategoryFormTailwind
          ref={categoryFormRef}
          category={editingCategory}
          onSave={handleSaveCategory}
          onCancel={handleCloseModal}
          isSaving={isSaving}
          onFormValidityChange={setIsFormValid}
        />
      </AdminModalTailwind>

      {/* Bestätigungsmodal für Löschen */}
      <ConfirmationModal
        isOpen={showConfirmation}
        onClose={handleCloseConfirmation}
        onConfirm={handleConfirmDelete}
        title="Kategorie löschen"
        subtitle="Diese Aktion kann nicht rückgängig gemacht werden."
        message={`Möchten Sie die Kategorie "${categoryToDelete?.name}" wirklich löschen?`}
        confirmText="Löschen"
        cancelText="Abbrechen"
        type="danger"
        layout="inline"
      />

      {/* Toast */}
      <Toast
        message={toast.message}
        type={toast.type}
        isVisible={toast.show}
        onClose={hideToast}
      />
    </>
  );
};

export default CategoriesTailwind;